/**
 * stock.ts — Pure computation of the current stock valuation the Stock report
 * shows: every tracked product with its on-hand quantity, the capital tied up
 * in it (at cost), and low/zero-stock alerts.
 *
 * Two rules this file pins down, both easy to get subtly wrong in a screen:
 *
 *  1. **Value is at cost, not retail.** Stock value = on-hand × purchase price.
 *     A valuation is what the stock cost you, not what you hope to sell it for.
 *  2. **"Low" excludes "out".** A product at or below its alert threshold is
 *     *low* only while it still has stock; once it hits zero it is *out of
 *     stock*, a separate, more urgent bucket. The two counts never overlap, and
 *     a zero alert threshold (feature off) is never "low".
 *
 * Pure — no DB access — so the valuation and bucketing rules are unit-testable.
 * `StockReportScreen` supplies the WatermelonDB products/categories and renders
 * the result.
 */

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface StockProductInput {
  id: string;
  name: string;
  sku: string | null;
  categoryId: string | null;
  currentStock: number;
  unit: string;
  purchasePrice: number;
  sellingPrice: number;
  lowStockAlert: number;
}

export interface StockCategoryInput {
  id: string;
  name: string;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface StockRow {
  id: string;
  name: string;
  sku: string | null;
  category: string;
  stock: number;
  unit: string;
  purchasePrice: number;
  sellingPrice: number;
  /** Capital locked at cost: stock × purchasePrice. */
  stockValue: number;
  lowStockAlert: number;
  /** At or below the alert threshold, while still in stock. */
  isLow: boolean;
}

export interface StockReport {
  /** All tracked products, sorted by name. */
  rows: StockRow[];
  /** Total capital in stock at cost. */
  totalValue: number;
  totalProducts: number;
  /** Products low but not yet out. */
  lowStockCount: number;
  /** Products at or below zero on hand. */
  zeroStockCount: number;
}

export type StockFilter = 'all' | 'low' | 'zero';

const NO_CATEGORY = '—';

export function buildStockReport(
  products: StockProductInput[],
  categories: StockCategoryInput[],
): StockReport {
  const catMap = new Map(categories.map((c) => [c.id, c.name]));

  const rows: StockRow[] = products
    .map((p) => ({
      id: p.id,
      name: p.name,
      sku: p.sku,
      category: p.categoryId ? (catMap.get(p.categoryId) ?? NO_CATEGORY) : NO_CATEGORY,
      stock: p.currentStock,
      unit: p.unit,
      purchasePrice: p.purchasePrice,
      sellingPrice: p.sellingPrice,
      stockValue: round2(p.currentStock * p.purchasePrice),
      lowStockAlert: p.lowStockAlert,
      isLow: p.currentStock <= p.lowStockAlert && p.lowStockAlert > 0,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    rows,
    totalValue: round2(rows.reduce((s, r) => s + r.stockValue, 0)),
    totalProducts: rows.length,
    // Low but still in stock — the out-of-stock rows are counted separately so
    // the two KPIs never double-count the same product.
    lowStockCount: rows.filter((r) => r.isLow && r.stock > 0).length,
    zeroStockCount: rows.filter((r) => r.stock <= 0).length,
  };
}

/**
 * Filter the report rows by the toolbar filter and a name/SKU search.
 * Pure and separate from `buildStockReport` so the (cheap) filtering can rerun
 * on every keystroke without recomputing the (expensive) valuation.
 */
export function filterStockRows(
  rows: StockRow[],
  filter: StockFilter,
  search: string,
): StockRow[] {
  const q = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (filter === 'low' && !(r.isLow && r.stock > 0)) return false;
    if (filter === 'zero' && r.stock > 0) return false;
    if (q &&
        !r.name.toLowerCase().includes(q) &&
        !(r.sku?.toLowerCase().includes(q))) return false;
    return true;
  });
}
